import type { FormSLA } from '../../types';
import { humanizeCondition } from './conditionHumanizer';
import { SectionCard } from './SectionCard';

/**
 * SLA reference table. Conditions are rendered as plain-English clauses; the
 * raw encoded query (`priorityIN1,2^state=2^EQ`) is hidden from the UI —
 * non-tech reviewers should never see ServiceNow's encoded query syntax.
 *
 * Duration is reformatted from ServiceNow's `1970-01-02 00:00:00` shape into
 * `1 day` / `4 days` etc., matching the original app.
 */
export function SLATable({ slas }: { slas: FormSLA[] }) {
  return (
    <SectionCard
      title="SLA"
      count={slas.length}
      defaultOpen
      emptyHint="No SLAs configured for this form."
    >
      {slas.length > 0 && (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left">
                <Th className="w-[28%]">Name</Th>
                <Th className="w-[10%]">Type</Th>
                <Th className="w-[12%]">Duration</Th>
                <Th className="w-[25%]">Start condition</Th>
                <Th className="w-[25%]">Stop condition</Th>
              </tr>
            </thead>
            <tbody>
              {slas.map((s, i) => (
                <tr
                  key={i}
                  className={
                    'align-top border-t border-slate-100 ' +
                    (i % 2 === 1 ? '[&>td]:bg-slate-100/80' : '')
                  }
                >
                  <Td className="text-slate-900 font-medium">
                    {s.name || <Dash />}
                  </Td>
                  <Td>
                    {s.type ? (
                      <span className="inline-flex items-center text-[11px] uppercase tracking-wide text-brand-danone bg-brand-50 rounded px-1.5 py-0.5">
                        {s.type}
                      </span>
                    ) : (
                      <Dash />
                    )}
                  </Td>
                  <Td className="text-slate-700 whitespace-nowrap">
                    {formatDuration(s.duration) ?? <Dash />}
                  </Td>
                  <Td className="text-slate-700">
                    {humanizeCondition(s.start_condition) ?? <Dash />}
                  </Td>
                  <Td className="text-slate-700">
                    {humanizeCondition(s.stop_condition) ?? <Dash />}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/**
 * ServiceNow stores durations as a relative timestamp counted from epoch
 * (`1970-01-02 00:00:00` = 1 day). Translate into a human delta. If the
 * shape isn't recognised we just return the raw string.
 */
function formatDuration(raw: string | null): string | null {
  if (!raw) return null;
  const m = raw.match(/^1970-01-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (!m) return raw;
  const days = Number(m[1]) - 1;
  const hours = Number(m[2]);
  const mins = Number(m[3]);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (mins > 0) parts.push(`${mins} minute${mins === 1 ? '' : 's'}`);
  return parts.length === 0 ? '0' : parts.join(' ');
}

function Th({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th className={'pr-4 pb-2 font-semibold ' + className} scope="col">
      {children}
    </th>
  );
}

function Td({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={'py-2.5 pr-4 align-top ' + className}>{children}</td>;
}

function Dash() {
  return <span className="text-slate-300">—</span>;
}
